import type { GtfsAsDict } from '@/@types/gtfs-object';

/**
 * Open indexedDB
 */
function openIndexedDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('gtfsDatabase', 1);

    // Emitted if the database doesn't exist or the version number has changed on the existing database
    request.onupgradeneeded = function (event) {
      console.log('running indexedDB onupgradeneeded');

      const db = (event.target as IDBOpenDBRequest).result;
      db.createObjectStore('gtfs');
    };

    // Emitted if the database already exists and its version number hasn't changed
    request.onsuccess = function (event) {
      const db = (event.target as IDBOpenDBRequest).result;
      resolve(db);
    };

    // Emitted if the database can't be opened
    request.onerror = function (event) {
      console.warn("can't open indexedDB", (event.target as IDBOpenDBRequest).error);
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
}

/**
 * Store a gtfs on indexedDB, best-effort: the cache is an optimisation, so a WebView without
 * IndexedDB must never fail a GTFS load that already succeeded — it just reloads from the API.
 */
async function setGtfsIDB(key: string, gtfsData: GtfsAsDict) {
  try {
    const gtfsDB = await openIndexedDB();
    const transaction = gtfsDB.transaction('gtfs', 'readwrite');
    const objectStore = transaction.objectStore('gtfs');
    const addGtfsToGtfsStore = objectStore.put(gtfsData, key);

    addGtfsToGtfsStore.onerror = function (event) {
      console.warn('Error while adding gtfs to IndexedDB', (event.target as IDBOpenDBRequest).error);
    };
  } catch (e) {
    console.warn('Failed to cache GTFS in IndexedDB, GTFS cache disabled', e);
  }
}

/**
 * Get a gtfs from indexedDB
 */
async function getGtfsIDB(key: string): Promise<GtfsAsDict> {
  const gtfsDB = await openIndexedDB();
  const transaction = gtfsDB.transaction('gtfs', 'readwrite');
  const objectStore = transaction.objectStore('gtfs');

  return new Promise((resolve, reject) => {
    const request = objectStore.get(key);
    request.onsuccess = function (event) {
      resolve((event.target as IDBOpenDBRequest).result as GtfsAsDict);
    };
    request.onerror = event => {
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
}

export default {
  setGtfsIDB,
  getGtfsIDB,
};
